import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

api.interceptors.request.use((config) => {
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }

  const adminToken = localStorage.getItem('adminToken');
  if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  }
  return config;
});


// Admin authentication functions
export const adminLogin = async (credentials) => {
  try {
    const response = await api.post('/auth/admin/login', credentials);
    if (response.data?.token) {
      localStorage.setItem('adminToken', response.data.token);
    }
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Login failed' };
  }
};

export const adminLogout = async () => {
  try {
    const response = await api.post('/auth/admin/logout');
    localStorage.removeItem('adminToken');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Logout failed' };
  }
};

export const verifySession = async () => {
  try {
    const response = await api.get('/auth/admin/verify-session');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Session verification failed' };
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/auth/admin/forgot-password', { email });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Password reset request failed' };
  }
};

export const resetPassword = async (token, password) => {
  try {
    const response = await api.post('/auth/admin/reset-password', { token, password });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Password reset failed' };
  }
};

export const updatePassword = async (passwordData) => {
  try {
    const response = await api.put('/auth/admin/update-password', passwordData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Password update failed' };
  }
};

// Dashboard statistics
export const getDashboardStats = async () => {
  try {
    const response = await api.get('/auth/admin/dashboard-stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch dashboard statistics' };
  }
};

// User management
export const getUsers = async (page = 1, limit = 10, search = '', filters = {}) => {
  try {
    const params = { page, limit, search };
    if (filters.profileComplete) params.profileComplete = filters.profileComplete;
    if (filters.contractStatus) params.contractStatus = filters.contractStatus;
    if (filters.contentType?.length) params.contentType = filters.contentType.join(',');
    if (filters.followersMin !== undefined && filters.followersMin !== '') params.followersMin = filters.followersMin;
    if (filters.followersMax !== undefined && filters.followersMax !== '') params.followersMax = filters.followersMax;
    const response = await api.get('/auth/admin/users', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch users' };
  }
};

export const getUserDetails = async (userId) => {
  try {
    const response = await api.get(`/auth/admin/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch user details' };
  }
};

export const updateUser = async (userId, payload) => {
  try {
    const response = await api.patch(`/auth/admin/users/${userId}`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update user' };
  }
};

export const toggleUserStatus = async (userId, status) => {
  try {
    const response = await api.patch('/auth/admin/users/toggle-status', {
      userId,
      status
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update user status' };
  }
};

// Export users data
export const exportUsers = async () => {
  try {
    const response = await api.get('/auth/admin/users/export');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to export users' };
  }
};

// Company management
export const getCompanies = async (page = 1, limit = 10, search = '') => {
  try {
    const response = await api.get('/auth/admin/companies', {
      params: { page, limit, search }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch companies' };
  }
};

export const toggleCompanyVerification = async (companyId, isVerified) => {
  try {
    const response = await api.patch('/auth/admin/companies/toggle-verification', {
      companyId,
      isVerified
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update company verification' };
  }
};

// Admin management
export const createAdmin = async (adminData) => {
  try {
    const response = await api.post('/auth/admin/create', adminData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to create admin' };
  }
};

// Affiliate data
export const getAffiliateStats = async () => {
  try {
    const response = await api.get('/auth/admin/affiliate-stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch affiliate statistics' };
  }
};

// Affiliate location stats (clicks by country/region). Params: { userId?, linkId?, shortCode? }
export const getAffiliateLocationStats = async (params = {}) => {
  try {
    const response = await api.get('/auth/admin/affiliate-location-stats', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch affiliate location statistics' };
  }
};

// Instagram Login Analytics
export const getInstagramLoginAnalytics = async () => {
  try {
    const response = await api.get('/auth/admin/analytics/instagram-login');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch Instagram login analytics' };
  }
};

// Marketplace Influencer Management
export const getMarketplaceInfluencers = async (page = 1, limit = 10, search = '', category = '', platform = '', isActive = undefined) => {
  try {
    const params = { page, limit };
    if (search) params.search = search;
    if (category) params.category = category;
    if (platform) params.platform = platform;
    if (isActive !== undefined) params.isActive = isActive;
    
    const response = await api.get('/admin/marketplace/influencers', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch marketplace influencers' };
  }
};

export const getMarketplaceInfluencerById = async (id) => {
  try {
    const response = await api.get(`/admin/marketplace/influencers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch influencer details' };
  }
};

export const createMarketplaceInfluencer = async (influencerData) => {
  try {
    const response = await api.post('/admin/marketplace/influencers', influencerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to create influencer' };
  }
};

export const updateMarketplaceInfluencer = async (id, influencerData) => {
  try {
    const response = await api.put(`/admin/marketplace/influencers/${id}`, influencerData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update influencer' };
  }
};

export const deleteMarketplaceInfluencer = async (id) => {
  try {
    const response = await api.delete(`/admin/marketplace/influencers/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to delete influencer' };
  }
};

export const toggleMarketplaceInfluencerStatus = async (id) => {
  try {
    const response = await api.patch(`/admin/marketplace/influencers/${id}/toggle-status`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to toggle influencer status' };
  }
};

export const bulkUploadMarketplaceInfluencers = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Don't set Content-Type header - axios will set it automatically with boundary for FormData
    const response = await api.post('/admin/marketplace/influencers/bulk-upload', formData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to upload influencers' };
  }
};

export const getMarketplaceStatistics = async () => {
  try {
    const response = await api.get('/admin/marketplace/influencers/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch marketplace statistics' };
  }
};

export const exportMarketplaceInfluencers = async (
  search = '',
  category = '',
  platform = '',
  isActive = undefined
) => {
  try {
    const params = {};
    if (search) params.search = search;
    if (category) params.category = category;
    if (platform) params.platform = platform;
    if (isActive !== undefined) params.isActive = isActive;

    const response = await api.get('/admin/marketplace/influencers/export-zip', {
      params,
      responseType: 'blob'
    });

    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    const decodedFilename = filenameMatch?.[1]
      ? decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim())
      : `marketplace-influencers-${new Date().toISOString().slice(0, 10)}.zip`;

    return {
      success: true,
      data: response.data,
      filename: decodedFilename,
      mimeType: response.headers['content-type'] || 'application/zip'
    };
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        const parsed = JSON.parse(await error.response.data.text());
        throw parsed || { error: 'Failed to export marketplace influencers ZIP' };
      } catch {
        throw { error: 'Failed to export marketplace influencers ZIP' };
      }
    }

    throw error.response?.data || { error: 'Failed to export marketplace influencers ZIP' };
  }
};

// Display Ad Network
export const getDisplayAds = async ({ page = 1, limit = 10, search = '', status = '' } = {}) => {
  try {
    const params = { page, limit };
    if (search) params.search = search;
    if (status) params.status = status;
    const response = await api.get('/adnetwork/admin/ads', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch advertisements' };
  }
};

export const getDisplayAdStats = async () => {
  try {
    const response = await api.get('/adnetwork/admin/ads/stats');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch advertisement statistics' };
  }
};

export const getDisplayAdById = async (id) => {
  try {
    const response = await api.get(`/adnetwork/admin/ads/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch advertisement' };
  }
};

export const createDisplayAd = async (formData) => {
  try {
    const response = await api.post('/adnetwork/admin/ads', formData, {
      timeout: 600000
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to create advertisement' };
  }
};

export const updateDisplayAd = async (id, formData) => {
  try {
    const response = await api.put(`/adnetwork/admin/ads/${id}`, formData, {
      timeout: 600000
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update advertisement' };
  }
};

export const deleteDisplayAd = async (id) => {
  try {
    const response = await api.delete(`/adnetwork/admin/ads/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to delete advertisement' };
  }
};

export const toggleDisplayAdStatus = async (id) => {
  try {
    const response = await api.patch(`/adnetwork/admin/ads/${id}/toggle-status`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to toggle advertisement status' };
  }
};

// Create the service object
const adminService = {
  adminLogin,
  adminLogout,
  verifySession,
  forgotPassword,
  resetPassword,
  updatePassword,
  getDashboardStats,
  getUsers,
  getUserDetails,
  updateUser,
  toggleUserStatus,
  getCompanies,
  toggleCompanyVerification,
  createAdmin,
  getAffiliateStats,
  getAffiliateLocationStats,
  getInstagramLoginAnalytics,
  exportUsers,
  getMarketplaceInfluencers,
  getMarketplaceInfluencerById,
  createMarketplaceInfluencer,
  updateMarketplaceInfluencer,
  deleteMarketplaceInfluencer,
  toggleMarketplaceInfluencerStatus,
  bulkUploadMarketplaceInfluencers,
  getMarketplaceStatistics,
  exportMarketplaceInfluencers,
  getDisplayAds,
  getDisplayAdStats,
  getDisplayAdById,
  createDisplayAd,
  updateDisplayAd,
  deleteDisplayAd,
  toggleDisplayAdStatus,
};

export default adminService; 
